import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetOffers } from "../../hooks/useOffer";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import "./OfferList.css";
import SafeImage from "../../components/common/SafeImage";

const getImageUrl = (offer) => {
  const raw = offer?.image_path;
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Absolute URL
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Already points to a public path like storage/offers/... or offers/...
  const normalized = trimmed.replace(/^\/+/, "");
  if (/^(storage\/.+|offers\/.+|uploads\/.+|public\/.+)/i.test(normalized)) {
    return `${
      import.meta.env.VITE_API_URL || "http://localhost:8000"
    }/${normalized}`;
  }
  // Fallback to offers directory
  return `${
    import.meta.env.VITE_API_URL || "http://localhost:8000"
  }/offers/${normalized}`;
};

const fetchOfferById = async (id) => {
  const res = await axios.get(
    `${
      import.meta.env.VITE_API_URL || "http://localhost:8000"
    }/api/offers/${id}`
  );
  return res.data.data || res.data;
};

const OfferList = () => {
  const navigate = useNavigate();
   const location = useLocation();
  const { data: offers = [], isLoading, error, refetch } = useGetOffers();

  // Modal state
  const [showModal, setShowModal] = React.useState(false);
  const [selectedOfferId, setSelectedOfferId] = React.useState(null);
  // Delete confirm modal state
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [deleteOfferId, setDeleteOfferId] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);

  // Action loading state
  const [actionLoading, setActionLoading] = React.useState(false);
  const [toggleLoading, setToggleLoading] = React.useState(false);
  const [toggleLoadingId, setToggleLoadingId] = React.useState(null);

 

  // Fetch offer details for modal
  const { data: modalOffer, isLoading: modalLoading,refetch:refetchModel } = useQuery({
    queryKey: ["offer", selectedOfferId],
    queryFn: () => fetchOfferById(selectedOfferId),
    enabled: !!selectedOfferId,
  });

  
  // Action handlers
  //Edit event
  const handleEditClick = (id) => {
    navigate(`/addoffers/${id}`);
  };

  // Delete event
  const handleDeleteClick = (id) => {
    setDeleteOfferId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteOfferId) return;
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${
          import.meta.env.VITE_API_URL || "http://localhost:8000"
        }/api/offers/${deleteOfferId}`
      );
      await refetch();
      setShowDeleteModal(false);
      setDeleteOfferId(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
    setDeleteOfferId(null);
  };

  const handleToggleActive = async (offer) => {
    setToggleLoadingId(offer.id);
    try {
      await axios.put(
        `${
          import.meta.env.VITE_API_URL || "http://localhost:8000"
        }/api/offers/${offer.id}`,
        {
          is_active: !offer.is_active,
        }
      );
      refetch();
    } finally {
      setToggleLoadingId(null);
    }
  };

  const handleViewProducts = (offer) => {
    setSelectedOfferId(offer.id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOfferId(null);
  };

  if (isLoading) {
    return <div className="offerlist-loading">Loading offers...</div>;
  }

  if (error) {
    return <div className="offerlist-error">Error fetching offers.</div>;
  }

  return (
    <>
      {toggleLoading && (
        <div className="offerlist-action-loader">
          <div className="spinner"></div>
          <span style={{ marginLeft: 12 }}>Processing...</span>
        </div>
      )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 24,
          }}
        >
          <h2
            style={{ margin: 0, fontWeight: 700, fontSize: 24, color: "#222" }}
          >
            Offer Management
          </h2>
          <button
            className="offerlist-add-btn"
            style={{
              background: "#19c7b3",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "9px 18px",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
              boxShadow: "0 1px 4px #0001",
              transition: "background 0.2s",
            }}
            onClick={() => navigate("/addoffers")}
          >
            Add Offer
          </button>
        </div>
        <table
          className="offerlist-table"
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            background: "#fafbfc",
            borderRadius: 16,
            overflow: "hidden",
            fontSize: 15,
          }}
        >
          <thead>
            <tr style={{ background: "#f5f7fa" }}>
              <th
                style={{
                  padding: "8px 6px",
                  fontWeight: 600,
                  color: "#444",
                  textAlign: "left",
                }}
              >
                Image
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  fontWeight: 600,
                  color: "#444",
                  textAlign: "left",
                }}
              >
                Name
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  fontWeight: 600,
                  color: "#444",
                  textAlign: "left",
                }}
              >
                Type
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  fontWeight: 600,
                  color: "#444",
                  textAlign: "left",
                }}
              >
                Value
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  fontWeight: 600,
                  color: "#444",
                  textAlign: "left",
                }}
              >
                Start
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  fontWeight: 600,
                  color: "#444",
                  textAlign: "left",
                }}
              >
                End
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  fontWeight: 600,
                  color: "#444",
                  textAlign: "left",
                }}
              >
                Status
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  fontWeight: 600,
                  color: "#444",
                  textAlign: "left",
                }}
              >
                Products
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  fontWeight: 600,
                  color: "#444",
                  textAlign: "left",
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {offers.length > 0 ? (
              offers.map((offer) => (
                <tr
                  key={offer.id || Math.random()}
                  style={{
                    background: "#fff",
                    borderBottom: "1px solid #f0f0f0",
                    transition: "background 0.2s",
                  }}
                >
                  <td style={{ textAlign: "center", padding: "7px 0" }}>
                    {getImageUrl(offer) ? (
                      <SafeImage
                        src={getImageUrl(offer)}
                        alt={offer.name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #eee",
                          boxShadow: "0 1px 4px #0001",
                        }}
                      />
                    ) : (
                      <span style={{ color: "#bbb" }}>No Image</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "7px 6px",
                      color: "#2a2a2a",
                      fontWeight: 500,
                    }}
                  >
                    {offer?.name || "N/A"}
                  </td>
                  <td style={{ padding: "7px 6px", color: "#555" }}>
                    {offer?.offer_type || offer?.type || "N/A"}
                  </td>
                  <td style={{ padding: "7px 6px", color: "#555" }}>
                    {offer?.value ?? "N/A"}
                  </td>
                  <td style={{ padding: "7px 6px", color: "#555" }}>
                    {offer?.start_date || "N/A"}
                  </td>
                  <td style={{ padding: "7px 6px", color: "#555" }}>
                    {offer?.end_date || "N/A"}
                  </td>
                  <td
                    style={{
                      padding: "7px 6px",
                      color: offer?.is_active ? "#19c7b3" : "#e74c3c",
                      fontWeight: 600,
                    }}
                  >
                    {offer?.is_active ? "Active" : "Inactive"}
                  </td>
                  <td style={{ padding: "7px 6px" }}>
                    <button
                      className="offerlist-view-btn"
                      style={{
                        border: "1.5px solid #222",
                        borderRadius: 6,
                        background: "#fff",
                        color: "#222",
                        fontWeight: 600,
                        fontSize: 14,
                        padding: "4px 12px",
                        cursor: "pointer",
                        transition: "background 0.2s, color 0.2s",
                      }}
                      onClick={() => handleViewProducts(offer)}
                    >
                      View
                    </button>
                  </td>
                  <td style={{ padding: "7px 6px" }}>
                    <button
                      onClick={() => handleEditClick(offer.id)}
                      style={{
                        marginRight: 6,
                        background: "#fff",
                        border: "1px solid #EF7722",
                        color: "#EF7722",
                        borderRadius: 5,
                        padding: "3px 10px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(offer.id)}
                      style={{
                        marginRight: 6,
                        background: "#fff",
                        border: "1px solid #e74c3c",
                        color: "#e74c3c",
                        borderRadius: 5,
                        padding: "3px 10px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => handleToggleActive(offer)}
                      className={
                        toggleLoadingId === offer.id ? "button-loading" : ""
                      }
                      style={{
                        background: offer.is_active ? "#e74c3c" : "#19c7b3",
                        color: "#fff",
                        border: "none",
                        borderRadius: 5,
                        padding: "3px 10px",
                        fontWeight: 600,
                        cursor: "pointer",
                        minWidth: 90,
                        transition: "background 0.3s, color 0.3s",
                      }}
                      disabled={toggleLoadingId === offer.id}
                    >
                      {toggleLoadingId === offer.id ? (
                        <span className="spinner"></span>
                      ) : offer.is_active ? (
                        "Deactivate"
                      ) : (
                        "Activate"
                      )}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="9"
                  className="offerlist-empty"
                  style={{ textAlign: "center", color: "#bbb", padding: 32 }}
                >
                  No offers found
                </td>
              </tr>
            )}
          </tbody>
        </table>

      {/* Modal for offer details */}
      {showModal && (
        <div className="offer-modal-overlay" onClick={handleCloseModal}>
          <div className="offer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={handleCloseModal}>
              ×
            </button>
            {modalLoading || !modalOffer ? (
              <div>Loading...</div>
            ) : (
              <>
                <h2 style={{ marginBottom: 0 }}>{modalOffer.name}</h2>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 32,
                    margin: "18px 0 24px 0",
                  }}
                >
                  {getImageUrl(modalOffer) && (
                    <SafeImage
                      src={getImageUrl(modalOffer)}
                      alt={modalOffer.name}
                      style={{
                        width: 200,
                        height: 200,
                        objectFit: "cover",
                        borderRadius: 12,
                        border: "1px solid #eee",
                        boxShadow: "0 2px 8px #0001",
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      minWidth: 260,
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600, color: "#555" }}>
                        Type:
                      </span>{" "}
                      <span style={{ color: "#222" }}>
                        {modalOffer.type || modalOffer.offer_type || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, color: "#555" }}>
                        Discount Type:
                      </span>{" "}
                      <span style={{ color: "#222" }}>
                        {modalOffer.discount_type || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, color: "#555" }}>
                        Value:
                      </span>{" "}
                      <span style={{ color: "#222" }}>
                        {modalOffer.value ?? "N/A"}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, color: "#555" }}>
                        Start Date:
                      </span>{" "}
                      <span style={{ color: "#222" }}>
                        {modalOffer.start_date || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, color: "#555" }}>
                        End Date:
                      </span>{" "}
                      <span style={{ color: "#222" }}>
                        {modalOffer.end_date || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, color: "#555" }}>
                        Status:
                      </span>{" "}
                      <span
                        style={{
                          color: modalOffer.is_active ? "#19c7b3" : "#e74c3c",
                          fontWeight: 600,
                        }}
                      >
                        {modalOffer.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
                <h3 style={{ marginTop: 24 }}>Products</h3>
                {modalOffer.products && modalOffer.products.length > 0 ? (
                  modalOffer.offer_type === "combo" ? (
                    (() => {
                      // Group products by vendor_id
                      const grouped = {};
                      modalOffer.products.forEach((product) => {
                        const vendorId = product.vendor?.vendor_id;
                        const vendorName =
                          product.vendor?.name ||
                          product.vendor?.vendor_name ||
                          "N/A";
                        if (!grouped[vendorId])
                          grouped[vendorId] = {
                            vendorName,
                            products: new Set(),
                          };
                        grouped[vendorId].products.add(
                          product.product_name || product.name || "N/A"
                        );
                      });
                      return (
                        <table
                          className="offer-modal-products-table"
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            marginTop: 8,
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  textAlign: "left",
                                  padding: "6px 8px",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                Vendor
                              </th>
                              <th
                                style={{
                                  textAlign: "left",
                                  padding: "6px 8px",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                Products
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.values(grouped).map((group, idx) => (
                              <tr key={group.vendorName + "-" + idx}>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    fontWeight: "bold",
                                    verticalAlign: "middle",
                                    background: "#fafbfc",
                                  }}
                                >
                                  {group.vendorName}
                                </td>
                                <td style={{ padding: "6px 8px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {Array.from(group.products).map(
                                      (prod, i) => (
                                        <span
                                          key={prod + "-" + i}
                                          className="vendor-badge-modal"
                                        >
                                          {prod}
                                        </span>
                                      )
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()
                  ) : (
                    (() => {
                      // Group products by product_id (single offer)
                      const grouped = {};
                      modalOffer.products.forEach((product) => {
                        const pid = product.product_id;
                        if (!grouped[pid])
                          grouped[pid] = {
                            product_name:
                              product.product_name || product.name || "N/A",
                            vendors: [],
                          };
                        grouped[pid].vendors.push(
                          product.vendor
                            ? product.vendor.name ||
                                product.vendor.vendor_name ||
                                "N/A"
                            : "N/A"
                        );
                      });
                      return (
                        <table
                          className="offer-modal-products-table"
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            marginTop: 8,
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  textAlign: "left",
                                  padding: "6px 8px",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                Product
                              </th>
                              <th
                                style={{
                                  textAlign: "left",
                                  padding: "6px 8px",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                Vendors
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.values(grouped).map((group, idx) => (
                              <tr key={group.product_name + "-" + idx}>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    fontWeight: "bold",
                                    verticalAlign: "middle",
                                    background: "#fafbfc",
                                  }}
                                >
                                  {group.product_name}
                                </td>
                                <td style={{ padding: "6px 8px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {group.vendors.map((vendor, i) => (
                                      <span
                                        key={vendor + "-" + i}
                                        className="vendor-badge-modal"
                                      >
                                        {vendor}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()
                  )
                ) : (
                  <p>No products found for this offer.</p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="offer-modal-overlay" onClick={handleDeleteCancel}>
          <div
            className="offer-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 420 }}
          >
            <button className="close-btn" onClick={handleDeleteCancel}>
              ×
            </button>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Delete Offer</h3>
            <p style={{ marginTop: 0 }}>
              Are you sure you want to delete this offer? This action cannot be
              undone.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 16,
              }}
            >
              <button
                onClick={handleDeleteCancel}
                style={{
                  background: "#f5f7fa",
                  border: "1px solid #bbb",
                  borderRadius: 6,
                  padding: "6px 14px",
                  fontWeight: 600,
                  cursor: deleteLoading ? "not-allowed" : "pointer",
                }}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  background: "#e74c3c",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 14px",
                  fontWeight: 700,
                  cursor: deleteLoading ? "not-allowed" : "pointer",
                }}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OfferList;
